import type { MouseEvent, ReactNode } from "react";
import { MemoryRouter } from "react-router-dom";
import { MemoryHistoryLeaveGuard } from "./HistoryLeaveGuard";
import { openModifiedLinkInWorkspace } from "./workspaceLinks";
import { TenantRouter } from "../contexts/TenantRouter";
import { usePlatform } from "../platform/PlatformProvider";

export interface RouterAdapterProps {
    children: ReactNode;
    initialPath?: string;
    tabId?: string;
}

export function RouterAdapter({
    children,
    initialPath = "/",
    tabId = initialPath,
}: RouterAdapterProps) {
    const platform = usePlatform();
    if (platform.navigation.router === "browser") {
        // TenantRouter is a BrowserRouter carrying the company/project basename
        // (PP-827). The desktop's MemoryRouter has no URL to credential, so it
        // keeps the plain router below.
        return <TenantRouter>{children}</TenantRouter>;
    }
    // WebView2's opener sends modified links to the system browser. App routes belong to
    // workspace tabs/windows; ordinary clicks stay with React Router.
    const openInternalTab = (event: MouseEvent<HTMLDivElement>) =>
        openModifiedLinkInWorkspace(platform, event);
    return (
        <MemoryRouter key={tabId} initialEntries={[initialPath]}>
            <MemoryHistoryLeaveGuard>
                <div
                    style={{ display: "contents" }}
                    onClick={openInternalTab}
                    onAuxClick={openInternalTab}
                >
                    {children}
                </div>
            </MemoryHistoryLeaveGuard>
        </MemoryRouter>
    );
}
